/**
 * One worker, one world, tiles on demand.
 *
 * The map is drawn by asking the *game's own* generator what the ground is
 * at a point - `probeColumn` - rather than by a second generator written to
 * agree with it. A map that draws its own idea of the country is a map that
 * will quietly stop matching the game, and the whole point of this page is
 * to tell somebody where to walk.
 *
 * That honesty is not free: a probe is about seventeen microseconds, so the
 * whole four-thousand-block world at one block to the pixel is a quarter of
 * an hour of arithmetic. Hence tiles, hence several workers, and hence a
 * zoom that decides how many blocks each pixel stands for: an overview of
 * the entire world at sixteen blocks a pixel is a quarter of a second, and
 * you only pay for detail where you actually look.
 *
 * The world is built once per seed and kept. Building it is the expensive
 * part (`generate()` grows the country around the start and cuts the замок
 * into its mountain, which is the only way to know where the замок is), and
 * a worker that rebuilt it per tile would spend all its time doing that.
 */
import { World } from "../core/world/world.js";
import { ADVENTURE } from "../core/world/maps.js";
import { SNOW_LINE } from "../core/world/biome.js";
import { castleCell, APPROACH_REACH } from "../core/world/castle.js";
import type { Biome } from "../core/world/biome.js";

export const TILE_PX = 128;

/** What the page asks for. */
export interface TileRequest {
  readonly t: "tile";
  readonly seed: number;
  /** Tile coordinates, in tiles, at this scale. */
  readonly tx: number;
  readonly tz: number;
  /** Blocks to the pixel. Bigger is further away. */
  readonly step: number;
  /** So a tile that arrives after the view has moved on can be dropped. */
  readonly epoch: number;
}

/** Everything about a world that is not a picture of it. */
export interface WorldFacts {
  readonly t: "world";
  readonly seed: number;
  readonly home: { x: number; z: number };
  /** Where somebody actually walks in, which is not where the box begins. */
  readonly gate: { x: number; y: number; z: number } | null;
  /** The far end of the road up to it. */
  readonly road: { x: number; z: number } | null;
  /**
   * Every село, because you cannot walk four thousand blocks looking.
   *
   * One to every five hundred by five hundred, and only where the country is
   * flat and dry enough, so the list is short and uneven - which is the
   * thing worth seeing on a map.
   */
  readonly villages: readonly { x: number; z: number }[];
  /**
   * Every лігво Змія Горинича, one to a thousand blocks square.
   *
   * With the rest rather than after it, unlike the скарби: whether a
   * mountain will hold a hall is a question about the shape of the ground,
   * and the ground is noise. Nothing has to be carved to answer it.
   */
  readonly lairs: readonly { x: number; y: number; z: number }[];
  readonly span: number;
  readonly ms: number;
}

/**
 * Where the скарби are, sent after the rest.
 *
 * Its own message because it costs three seconds and the map does not: a
 * treasure is only real if there is rock where it would go, and whether a
 * cave has eaten that block is a fact about blocks rather than about noise,
 * so nearly two hundred columns have to be grown to answer it. Holding the
 * whole page for that would be paying for the rings before the country.
 */
export interface TreasureFacts {
  readonly t: "treasures";
  readonly seed: number;
  readonly list: readonly { x: number; y: number; z: number; depth: number }[];
  readonly ms: number;
}

export interface TileReply {
  readonly t: "tile";
  readonly tx: number;
  readonly tz: number;
  readonly step: number;
  readonly epoch: number;
  readonly pixels: ArrayBuffer;
}

/*
 * The country's colours.
 *
 * Not the block textures averaged - the game's own ground is brown dirt and
 * grey stone under grass, and a map made of that reads as mud. These are the
 * four countries as they look from far enough up to see them all at once,
 * shaded afterwards by how high the ground is, which is what turns a flat
 * wash of green into hills.
 */
const COUNTRY: Record<Biome, readonly [number, number, number]> = {
  meadow: [88, 124, 62],
  mountain: [122, 118, 110],
  winter: [206, 214, 220],
  desert: [196, 174, 116],
};
const WATER_DEEP: readonly [number, number, number] = [30, 56, 86];
const WATER_SHALLOW: readonly [number, number, number] = [58, 104, 140];
const SNOW: readonly [number, number, number] = [232, 238, 244];

let world: World | null = null;
let builtFor: number | null = null;
let grownFor: number | null = null;

/**
 * The world for this seed, built once and kept.
 *
 * `grow` is the whole performance story of this page. `probeColumn` is pure
 * noise - measured against a grown world, eight hundred probes agreed to the
 * block - so a worker that only paints tiles never calls `generate()` and
 * never allocates a chunk. `generate()` costs about one and a half seconds
 * and tens of megabytes, and the only thing it is needed for is `castleAt`:
 * the замок is cut into a real mountain by writing real blocks, so finding
 * it means doing it. One worker pays that once; the tile workers never do.
 */
function worldFor(seed: number, grow = false): World {
  if (world === null || builtFor !== seed) {
    world = new World({
      seed, sizeXZ: ADVENTURE.sizeXZ, sizeY: ADVENTURE.sizeY,
      kremlin: false, terrain: "adventure", adult: false,
    });
    builtFor = seed;
    grownFor = null;
  }
  if (grow && grownFor !== seed) {
    world.generate();
    grownFor = seed;
  }
  return world;
}

/**
 * Paint one tile.
 *
 * The shading is the slope read off the neighbour one step to the north-west,
 * the oldest trick there is for making a height field look like country: a
 * face tilted towards the light is brightened and one tilted away is darkened.
 * Without it a mountain range and a meadow are the same flat colour, which is
 * exactly the thing a person is scanning this map for.
 */
function paint(seed: number, tx: number, tz: number, step: number): Uint8ClampedArray {
  const w = worldFor(seed);
  const sea = w.seaLevel;
  const out = new Uint8ClampedArray(TILE_PX * TILE_PX * 4);
  const x0 = tx * TILE_PX * step;
  const z0 = tz * TILE_PX * step;
  const span = ADVENTURE.sizeXZ * 16;

  for (let py = 0; py < TILE_PX; py++) {
    const z = z0 + py * step;
    for (let px = 0; px < TILE_PX; px++) {
      const x = x0 + px * step;
      const at = (py * TILE_PX + px) * 4;
      if (x < 0 || z < 0 || x >= span || z >= span) {
        out[at] = 14; out[at + 1] = 17; out[at + 2] = 22; out[at + 3] = 255;
        continue;
      }
      // Floored, because below a block to the pixel `x` is a fraction and
      // the noise would happily interpolate one - which is a smooth picture
      // of a country made of blocks. Zooming in should show bigger blocks,
      // not a blur.
      const here = w.probeColumn(Math.floor(x), Math.floor(z));
      let rgb: readonly [number, number, number];
      if (here.water !== null) {
        // How deep, so a shore reads as a shore and a lake has a middle.
        const deep = Math.min(1, (here.water - here.ground) / 8);
        rgb = [
          WATER_SHALLOW[0] + (WATER_DEEP[0] - WATER_SHALLOW[0]) * deep,
          WATER_SHALLOW[1] + (WATER_DEEP[1] - WATER_SHALLOW[1]) * deep,
          WATER_SHALLOW[2] + (WATER_DEEP[2] - WATER_SHALLOW[2]) * deep,
        ];
        out[at] = rgb[0]; out[at + 1] = rgb[1]; out[at + 2] = rgb[2];
        out[at + 3] = 255;
        continue;
      }
      /*
       * Snow as a wash over the country, not instead of it.
       *
       * The game's own rule is a line: above it the ground is снiг and below
       * it is not, which is right for a block you stand on and wrong for a
       * map - half the world came out flat white and the mountains stopped
       * reading as mountains. Blending it on over the last dozen blocks
       * keeps the country underneath visible and still says where the snow
       * starts.
       */
      const base = COUNTRY[here.biome];
      const above = here.ground - sea - SNOW_LINE;
      const white = Math.max(0, Math.min(1, above / 12));
      rgb = white === 0 ? base : [
        base[0] + (SNOW[0] - base[0]) * white,
        base[1] + (SNOW[1] - base[1]) * white,
        base[2] + (SNOW[2] - base[2]) * white,
      ];
      // Height, then slope. Height alone makes the high ground pale; slope
      // is what actually draws the ridges.
      const lift = 1 + Math.max(-0.25, Math.min(0.25, (here.ground - sea - 12) / 90));
      const up = x >= 1 && z >= 1
        ? w.probeColumn(Math.floor(x - Math.max(1, step)),
                        Math.floor(z - Math.max(1, step))).ground
        : here.ground;
      const slope = Math.max(-0.42, Math.min(0.42, (here.ground - up) / 7));
      const light = lift + slope;
      out[at] = rgb[0] * light;
      out[at + 1] = rgb[1] * light;
      out[at + 2] = rgb[2] * light;
      out[at + 3] = 255;
    }
  }
  return out;
}

self.onmessage = (e: MessageEvent<TileRequest | { t: "world"; seed: number }>) => {
  const message = e.data;
  if (message.t === "world") {
    const began = Date.now();
    const w = worldFor(message.seed, true);
    const home = w.islands[0] ?? { x: 0, z: 0 };
    const facts: WorldFacts = {
      t: "world", seed: message.seed,
      home: { x: Math.round(home.x), z: Math.round(home.z) },
      gate: w.castleAt === null ? null : {
        x: w.castleAt.x,
        y: castleCell(w.castleAt, [0, 1, 0]).y,
        z: castleCell(w.castleAt, [0, 0, 0]).z,
      },
      road: w.castleAt === null ? null : {
        x: w.castleAt.x,
        z: castleCell(w.castleAt, [0, 0, 0]).z - APPROACH_REACH,
      },
      // Noise only, so this costs a millisecond and does not need the
      // world that `generate()` built above it.
      villages: w.villages(),
      lairs: w.dragonLairs(),
      span: ADVENTURE.sizeXZ * 16,
      ms: Date.now() - began,
    };
    (self as unknown as Worker).postMessage(facts);
    /*
     * And then the treasures, which the page draws when they arrive.
     *
     * After the facts and not with them: the map is up and usable in a
     * second, and the rings appear over it a few seconds later.
     */
    const dug = Date.now();
    const list = w.treasures();
    const more: TreasureFacts = {
      t: "treasures", seed: message.seed, list, ms: Date.now() - dug,
    };
    (self as unknown as Worker).postMessage(more);
    return;
  }
  const pixels = paint(message.seed, message.tx, message.tz, message.step);
  const reply: TileReply = {
    t: "tile", tx: message.tx, tz: message.tz, step: message.step,
    epoch: message.epoch, pixels: pixels.buffer as ArrayBuffer,
  };
  (self as unknown as Worker).postMessage(reply, [reply.pixels]);
};
